import { useMemo, useState } from 'react';
import type { ColumnDef } from '@tanstack/react-table';
import { Download, Eye, X } from 'lucide-react';
import { toast } from 'sonner';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { PageHeader } from '@/components/ui/page-header';
import { DataTable } from '@/components/data-table/DataTable';
import { BulkActionBar, BulkButton } from '@/components/data-table/BulkActionBar';
import { KNOWN_AUDIT_TABLES, useAuditLog } from '@/hooks/queries/useAuditLog';
import { useUsersList } from '@/hooks/queries/useUsersList';
import { formatDateTimeDZ } from '@/lib/format';
import { t } from '@/locales/i18n';
import type { AuditAction, AuditLogRow } from '@/lib/database.types';

const ACTION_VARIANT: Record<AuditAction, 'success' | 'default' | 'destructive'> = {
  INSERT: 'success',
  UPDATE: 'default',
  DELETE: 'destructive',
};

export function AuditPage(): JSX.Element {
  const [filterTable, setFilterTable] = useState<string | null>(null);
  const [filterAction, setFilterAction] = useState<AuditAction | null>(null);
  const [filterUser, setFilterUser] = useState<string | null>(null);
  const [detailRow, setDetailRow] = useState<AuditLogRow | null>(null);
  const { data: users = [] } = useUsersList();
  const userMap = useMemo(() => {
    const m = new Map<string, string>();
    for (const u of users) m.set(u.user_id, u.full_name ?? u.user_id.slice(0, 8));
    return m;
  }, [users]);
  const {
    data: rows = [],
    isLoading,
    error,
  } = useAuditLog({
    table: filterTable,
    action: filterAction,
    userId: filterUser,
    limit: 500,
  });
  const [selectedRows, setSelectedRows] = useState<AuditLogRow[]>([]);
  const [selectionVersion, setSelectionVersion] = useState(0);
  const clearSelection = (): void => setSelectionVersion((v) => v + 1);

  const columns = useMemo<ColumnDef<AuditLogRow>[]>(
    () => [
      {
        accessorKey: 'ts',
        header: 'Horodatage',
        size: 160,
        cell: ({ getValue }) => (
          <span className="num text-xs">{formatDateTimeDZ(String(getValue()))}</span>
        ),
      },
      {
        accessorKey: 'action',
        header: 'Action',
        size: 100,
        cell: ({ getValue }) => {
          const a = getValue() as AuditAction;
          return <Badge variant={ACTION_VARIANT[a]}>{a}</Badge>;
        },
      },
      { accessorKey: 'table_name', header: 'Table', size: 160 },
      {
        accessorKey: 'record_id',
        header: 'Enregistrement',
        size: 200,
        cell: ({ getValue }) => (
          <span className="font-mono text-[10px]">{String(getValue() ?? '—')}</span>
        ),
      },
      {
        accessorKey: 'user_id',
        header: 'Utilisateur',
        size: 180,
        cell: ({ getValue }) => {
          const uid = getValue() as string | null;
          if (!uid) return <span className="text-xs text-muted-foreground">système</span>;
          const name = userMap.get(uid);
          return (
            <span className="text-xs">
              {name ?? <span className="font-mono text-[10px]">{uid.slice(0, 8)}…</span>}
            </span>
          );
        },
      },
      {
        id: 'diff',
        header: 'Modification',
        cell: ({ row }) => {
          const r = row.original;
          if (r.action === 'DELETE')
            return <span className="text-xs text-muted-foreground">(supprimé)</span>;
          const diff = r.diff;
          if (!diff || Object.keys(diff).length === 0) {
            return <span className="text-xs text-muted-foreground">—</span>;
          }
          const summary = Object.keys(diff).slice(0, 4).join(', ');
          return (
            <span className="text-xs text-muted-foreground">
              {summary}
              {Object.keys(diff).length > 4 ? ` (+${Object.keys(diff).length - 4})` : ''}
            </span>
          );
        },
      },
      {
        id: 'actions',
        header: '',
        size: 60,
        enableSorting: false,
        cell: ({ row }) => (
          <Button
            variant="ghost"
            size="icon"
            className="h-7 w-7"
            onClick={() => setDetailRow(row.original)}
            title="Voir le détail (avant/après)"
          >
            <Eye className="h-3.5 w-3.5" />
          </Button>
        ),
      },
    ],
    [userMap],
  );

  return (
    <div className="flex h-full flex-col">
      <PageHeader title={t('parametres.audit.title')} subtitle={t('parametres.audit.subtitle')} />
      <div className="space-y-4 p-6">
        <div className="flex flex-wrap items-end gap-3 rounded-md border border-border bg-card p-3">
          <div className="space-y-1">
            <label className="text-xs text-muted-foreground">
              {t('parametres.audit.filterTable')}
            </label>
            <select
              value={filterTable ?? ''}
              onChange={(e) => setFilterTable(e.target.value || null)}
              className="h-9 w-48 rounded-md border border-input bg-background px-2 text-sm"
            >
              <option value="">— Toutes —</option>
              {KNOWN_AUDIT_TABLES.map((t2) => (
                <option key={t2} value={t2}>
                  {t2}
                </option>
              ))}
            </select>
          </div>
          <div className="space-y-1">
            <label className="text-xs text-muted-foreground">
              {t('parametres.audit.filterAction')}
            </label>
            <select
              value={filterAction ?? ''}
              onChange={(e) => setFilterAction((e.target.value || null) as AuditAction | null)}
              className="h-9 w-32 rounded-md border border-input bg-background px-2 text-sm"
            >
              <option value="">— Toutes —</option>
              <option value="INSERT">INSERT</option>
              <option value="UPDATE">UPDATE</option>
              <option value="DELETE">DELETE</option>
            </select>
          </div>
          <div className="space-y-1">
            <label className="text-xs text-muted-foreground">Utilisateur</label>
            <select
              value={filterUser ?? ''}
              onChange={(e) => setFilterUser(e.target.value || null)}
              className="h-9 w-48 rounded-md border border-input bg-background px-2 text-sm"
            >
              <option value="">— Tous —</option>
              {users.map((u) => (
                <option key={u.user_id} value={u.user_id}>
                  {u.full_name ?? u.user_id.slice(0, 8)}
                </option>
              ))}
            </select>
          </div>
        </div>

        {error && (
          <div className="rounded-md border border-destructive/40 bg-destructive/10 px-3 py-2 text-xs text-destructive">
            Erreur de chargement : {(error as Error).message}
          </div>
        )}
        <DataTable
          key={selectionVersion}
          data={rows}
          columns={columns}
          loading={isLoading}
          emptyMessage="Aucune entrée d'audit pour ces filtres."
          pageSize={20}
          searchPlaceholder="Rechercher (table, action, ID…)"
          exportFileName="audit-log.csv"
          enableSelection
          getRowId={(row) => row.id}
          onSelectionChange={setSelectedRows}
          bulkBar={
            <BulkActionBar count={selectedRows.length} onClear={clearSelection}>
              <BulkButton
                onClick={() => {
                  exportAuditCSV(selectedRows);
                  toast.success(`${selectedRows.length} ligne(s) exportée(s)`);
                }}
              >
                <Download className="h-3.5 w-3.5" /> Exporter CSV (archivage)
              </BulkButton>
            </BulkActionBar>
          }
        />
      </div>

      {detailRow && (
        <AuditDetailDrawer
          row={detailRow}
          userName={detailRow.user_id ? (userMap.get(detailRow.user_id) ?? null) : null}
          onClose={() => setDetailRow(null)}
        />
      )}
    </div>
  );
}

function AuditDetailDrawer({
  row,
  userName,
  onClose,
}: {
  row: AuditLogRow;
  userName: string | null;
  onClose: () => void;
}): JSX.Element {
  return (
    <>
      <div className="fixed inset-0 z-40 bg-slate-900/30" onClick={onClose} aria-hidden />
      <aside
        className="fixed inset-y-0 right-0 z-50 flex w-full max-w-xl flex-col border-l border-slate-200 bg-white shadow-2xl"
        role="dialog"
      >
        <header className="flex items-start justify-between gap-3 border-b border-slate-200 p-4">
          <div className="min-w-0 flex-1">
            <div className="text-[10px] font-bold uppercase tracking-widest text-slate-500">
              Audit log · {formatDateTimeDZ(row.ts)}
            </div>
            <h2 className="mt-1 truncate text-lg font-bold text-slate-900">
              <Badge variant={ACTION_VARIANT[row.action]} className="mr-2">
                {row.action}
              </Badge>
              {row.table_name}
            </h2>
            <div className="mt-1 text-xs text-muted-foreground">
              <span className="font-medium">Utilisateur :</span>{' '}
              {userName ?? (row.user_id ? row.user_id.slice(0, 8) + '…' : 'système')}
              {row.record_id && (
                <>
                  {' · '}
                  <span className="font-medium">Record :</span>{' '}
                  <span className="font-mono text-[10px]">{row.record_id}</span>
                </>
              )}
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="rounded-md p-1.5 text-slate-500 hover:bg-slate-100"
            aria-label="Fermer"
          >
            <X className="h-4 w-4" />
          </button>
        </header>
        <div className="scrollbar-thin flex-1 space-y-4 overflow-y-auto p-4">
          {row.action === 'UPDATE' && row.diff && Object.keys(row.diff).length > 0 && (
            <section>
              <h3 className="mb-2 text-[10px] font-bold uppercase tracking-widest text-slate-500">
                Diff ({Object.keys(row.diff).length} champ
                {Object.keys(row.diff).length > 1 ? 's' : ''})
              </h3>
              <div className="overflow-hidden rounded-md border border-slate-200">
                <table className="w-full text-xs">
                  <thead className="bg-slate-50 text-[10px] font-bold uppercase text-slate-600">
                    <tr>
                      <th className="px-2 py-1.5 text-left">Champ</th>
                      <th className="px-2 py-1.5 text-left">Avant</th>
                      <th className="px-2 py-1.5 text-left">Après</th>
                    </tr>
                  </thead>
                  <tbody>
                    {Object.entries(
                      row.diff as Record<string, { old?: unknown; new?: unknown }>,
                    ).map(([field, change]) => (
                      <tr key={field} className="border-t border-slate-100">
                        <td className="px-2 py-1 font-mono text-[10px] text-slate-700">{field}</td>
                        <td className="px-2 py-1 font-mono text-[10px] text-rose-700">
                          {formatValue(change?.old)}
                        </td>
                        <td className="px-2 py-1 font-mono text-[10px] text-emerald-700">
                          {formatValue(change?.new)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </section>
          )}
          {row.action !== 'UPDATE' && (
            <section>
              <h3 className="mb-2 text-[10px] font-bold uppercase tracking-widest text-slate-500">
                {row.action === 'INSERT' ? 'Données insérées' : 'Données supprimées'}
              </h3>
              <pre className="max-h-96 overflow-auto rounded-md border border-slate-200 bg-slate-50 p-3 font-mono text-[10px] text-slate-700">
                {JSON.stringify(row.action === 'INSERT' ? row.after : row.before, null, 2)}
              </pre>
            </section>
          )}
          {row.ip_address && (
            <section className="text-[10px] text-muted-foreground">
              <span className="font-semibold">IP :</span>{' '}
              <span className="font-mono">{row.ip_address}</span>
            </section>
          )}
        </div>
      </aside>
    </>
  );
}

function formatValue(v: unknown): string {
  if (v == null) return '—';
  if (typeof v === 'object') return JSON.stringify(v);
  return String(v);
}

function exportAuditCSV(rows: AuditLogRow[]): void {
  if (rows.length === 0) return;
  const headers = ['ts', 'user_id', 'table_name', 'record_id', 'action', 'diff'];
  const csv = [
    headers.join(','),
    ...rows.map((r) =>
      headers
        .map((h) => {
          const v = (r as unknown as Record<string, unknown>)[h];
          const s = v == null ? '' : typeof v === 'object' ? JSON.stringify(v) : String(v);
          return /[",\n;]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
        })
        .join(','),
    ),
  ].join('\n');
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `audit-log-${rows.length}-${Date.now()}.csv`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}
